// The "How to improve" panel for a report card grade or an Insights pattern: what happened,
// what to do, the target and one-click actions. `onAction(action)` handles 'rule' (add it to
// the trader's rules) and 'test' (start an experiment); without it, only the steps show (the
// free report card, where there's no account to save to).
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';
import { useI18n } from './i18n';

// Adds a rule to the trader's rules list, unless it's already there.
export async function addRuleToRoutine(text) {
  const routine = await api('/routine');
  const rules = routine.rules || [];
  if (rules.some((r) => r.trim().toLowerCase() === text.trim().toLowerCase())) return 'exists';
  await api('/routine', { method: 'PUT', body: { rules: [...rules, text] } });
  return 'added';
}

function ActionButton({ action, onAction }) {
  const { t } = useI18n();
  const [state, setState] = useState('');
  async function run() {
    setState('busy');
    try {
      const r = await onAction(action);
      setState(r === 'exists' ? 'exists' : action.kind === 'rule' ? 'added' : '');
    } catch {
      setState('error');
    }
  }
  if (action.kind === 'rule') {
    return (
      <div className="improve-rule">
        <span className="improve-rule-text">“{action.text}”</span>
        <button type="button" className="secondary small" onClick={run} disabled={state === 'busy' || state === 'added' || state === 'exists'}>
          {state === 'added' ? `✓ ${t('Added to my rules')}` : state === 'exists' ? `✓ ${t('Already in my rules')}` : state === 'error' ? t('Try again') : `+ ${t('Add to my rules')}`}
        </button>
      </div>
    );
  }
  return (
    <button type="button" className="secondary small" onClick={run} disabled={state === 'busy'}>
      🧪 {t('Test it for 10 days')}: {action.tip}
    </button>
  );
}

// `embedded`: inside a report card grade that has grown to show it, so the card's own
// heading is the panel's heading.
export default function ImprovePanel({ title, grade, gradeClass, summary, plan, onClose, onAction, links = 'all', embedded = false }) {
  const { t } = useI18n();
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current.scrollIntoView) ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [title]);
  if (!plan) return null;
  const doable = plan.actions.filter((a) => (a.kind === 'link' ? links === 'all' || (links === 'tools' && a.to.startsWith('/tools')) : Boolean(onAction)));
  return (
    <section className={`improve-panel${embedded ? ' embedded' : ''}`} ref={ref} aria-label={t('How to improve {subject}', { subject: title })}>
      {!embedded && <div className="improve-head">
        <div>
          <span className="improve-eyebrow">{t('How to improve')}</span>
          <h3>{grade && <span className={`report-grade ${gradeClass || ''}`}>{grade}</span>} {title}</h3>
          {summary && <p className="improve-summary">{summary}</p>}
        </div>
        {onClose && <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>}
      </div>}

      <div className="improve-body">
        {plan.evidence.length > 0 && (
          <div className="improve-block">
            <h4>{t('What happened')}</h4>
            <ul className="improve-evidence">
              {plan.evidence.map((e) => (
                <li key={`${e.label}${e.value}`}><span>{e.label}</span><strong className={e.tone || ''}>{e.value}</strong></li>
              ))}
            </ul>
          </div>
        )}
        <div className="improve-block">
          <h4>{t('What to do')}</h4>
          <ol className="improve-steps">{plan.steps.map((step) => <li key={step}>{step}</li>)}</ol>
          {plan.target && <p className="improve-target"><strong>🎯 {t('Your target')}:</strong> {plan.target}</p>}
        </div>
      </div>

      {doable.length > 0 && (
        <div className="improve-actions">
          {doable.map((a) => (a.kind === 'link'
            ? <Link key={a.to} to={a.to} className="secondary small improve-link">{a.label} &rarr;</Link>
            : <ActionButton key={`${a.kind}${a.text || a.tip}`} action={a} onAction={onAction} />))}
        </div>
      )}
    </section>
  );
}
